import React, { useEffect, useMemo, useState } from "react";
import { shoppers as shoppersApi } from "../api/endpoints";
import type { User } from "../types";

type AllocationRow = {
  id: number;
  year: number;
  month: number;
  allocation: number;
};

export default function ShoppersPage() {
  const [shopperList, setShopperList] = useState<User[]>([]);
  const [selectedShopper, setSelectedShopper] = useState<User | null>(null);
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [draftAllocations, setDraftAllocations] = useState<Record<number, number>>({});
  const [year, setYear] = useState(new Date().getFullYear());
  const [loadingAllocations, setLoadingAllocations] = useState(false);
  const [savingMonth, setSavingMonth] = useState<number | null>(null);

  useEffect(() => {
    shoppersApi.list().then((res) => setShopperList(res.items));
  }, []);

  useEffect(() => {
    if (!selectedShopper) return;
    setLoadingAllocations(true);
    shoppersApi
      .getAllocations(selectedShopper.id, year)
      .then((rows) => {
        setAllocations(rows);
        const nextDrafts: Record<number, number> = {};
        for (let month = 1; month <= 12; month += 1) {
          nextDrafts[month] = rows.find((row) => row.month === month)?.allocation || 0;
        }
        setDraftAllocations(nextDrafts);
      })
      .finally(() => setLoadingAllocations(false));
  }, [selectedShopper, year]);

  const handleAllocationChange = (month: number, value: number) => {
    setDraftAllocations((prev) => ({
      ...prev,
      [month]: value,
    }));
  };

  const saveAllocation = async (month: number) => {
    if (!selectedShopper) return;
    const nextValue = Math.max(0, draftAllocations[month] || 0);
    const currentValue = allocations.find((row) => row.month === month)?.allocation || 0;
    if (nextValue === currentValue) return;

    setSavingMonth(month);
    await shoppersApi.setAllocation(selectedShopper.id, year, month, nextValue);
    setAllocations((prev) => {
      const existing = prev.find((row) => row.month === month);
      if (existing) {
        return prev.map((row) => (row.month === month ? { ...row, allocation: nextValue } : row));
      }
      return [...prev, { id: month, year, month, allocation: nextValue }];
    });
    setSavingMonth(null);
  };

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const selectedShopperName = useMemo(
    () => (selectedShopper ? `${selectedShopper.first_name} ${selectedShopper.last_name}` : ""),
    [selectedShopper]
  );

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Mystery Shoppers</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="border-b border-slate-200 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">Shopper Directory</div>
            <div className="mt-1 text-sm text-slate-500">
              Select a shopper to review or edit yearly allocations.
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Email</th>
              </tr>
            </thead>
            <tbody>
              {shopperList.map((s) => (
                <tr
                  key={s.id}
                  className={`border-t cursor-pointer hover:bg-gray-50 ${selectedShopper?.id === s.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedShopper(s)}
                >
                  <td className="px-4 py-3">{s.first_name} {s.last_name}</td>
                  <td className="px-4 py-3 text-xs">{s.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedShopper && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="font-bold">{selectedShopperName} - Allocations</h2>
                <div className="mt-1 text-sm text-slate-500">Edit the yearly allocation plan month by month.</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setYear((current) => current - 1)} className="border px-2 py-1 rounded text-sm">&lt;</button>
                <span className="px-2 py-1 font-bold">{year}</span>
                <button onClick={() => setYear((current) => current + 1)} className="border px-2 py-1 rounded text-sm">&gt;</button>
              </div>
            </div>
            {loadingAllocations ? (
              <div className="py-12 text-center text-sm text-slate-500">Loading allocations...</div>
            ) : (
            <div className="grid grid-cols-3 gap-3">
              {months.map((m, i) => {
                const month = i + 1;
                return (
                  <div key={i} className="border rounded p-3">
                    <div className="text-xs text-gray-500">{m}</div>
                    <input
                      type="number"
                      value={draftAllocations[month] ?? 0}
                      onChange={(e) => handleAllocationChange(month, Number(e.target.value))}
                      onBlur={() => saveAllocation(month)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.currentTarget.blur();
                        }
                      }}
                      className="w-full border rounded px-2 py-1 mt-1 text-sm"
                      min={0}
                    />
                    {savingMonth === month && (
                      <div className="mt-1 text-xs text-slate-400">Saving...</div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
